import { useEffect, useState } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { PaymentElement } from "@stripe/react-stripe-js";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";

const backend_domain = process.env.REACT_APP_BACKEND_DOMAIN;
const backend_port = process.env.REACT_APP_BACKEND_PORT;

interface PaymentProps {
  price: number;
}

export default function Payment() {
  const [data, setData] = useState<any>(""); // Use 'any' as a temporary solution
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { id } = useParams(); // web addres thingy
  const { user, isAuthenticated } = useAuth0();
  const [imageError, setImageError] = useState(false);

  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState("");
  const [isFree, setIsFree] = useState<boolean | null>(null);

  const hasPurchased =
    user && data.purchasedUserIDs && data.purchasedUserIDs.includes(user.sub);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname }
      });
    }
  }, [isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = await getAccessTokenSilently();

        const response = await fetch(
          `${backend_domain}${backend_port}/post/${id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const postData = await response.json();
        setData(postData);
        setIsFree(postData.price.price === 0);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [getAccessTokenSilently, id]);

  useEffect(() => {
    if (isFree === false) {
      fetch(`${backend_domain}${backend_port}/payment/config`).then(
        async (r) => {
          const { publishableKey } = await r.json();
          setStripePromise(loadStripe(publishableKey));
        }
      );

      getAccessTokenSilently().then(async (accessToken) => {
        const r = await fetch(
          `${backend_domain}${backend_port}/payment/create-payment-intent`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              submissionId: id,
              userId: user?.sub,
            }),
          }
        );
        if (r.ok) {
          const { clientSecret } = await r.json();
          setClientSecret(clientSecret);
        }
      });
    }
  }, [id, user?.sub, isFree, getAccessTokenSilently]);

  const handleImageError = () => {
    setImageError(true);
  };

  if (hasPurchased) {
    return (
      <div className="container mx-auto px-4 py-6 text-center">
        <div className="text-xl text-green-600">
          You have already bought this!
        </div>
      </div>
    );
  }

  return (
    <>
      {isAuthenticated && data && (
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <h1 className="font-bold text-2xl mb-6">Checkout</h1>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Product Details Section */}
            <div className="w-full md:w-1/2 bg-gray-200 rounded-xl p-4 md:p-6">
              <h2 className="font-semibold text-lg mb-4">Payment Details</h2>
              <div className="flex flex-col sm:flex-row items-center gap-4 border border-gray-300 rounded-lg p-4 bg-white">
                <div className="w-full sm:w-1/3">
                  <img
                    src={imageError ? "/logo192.png" : data.imgUrl}
                    alt={data.title}
                    className="w-full h-32 object-cover rounded-lg"
                    onError={handleImageError}
                  />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <div className="font-bold text-lg">{data.title}</div>
                  <div className="text-sm text-gray-600">
                    Content published by{" "}
                    <Link
                      to={`/profile/${data.username}`}
                      className="text-blue-600 hover:underline"
                    >
                      {data.username}
                    </Link>
                  </div>
                  <div className="text-xl font-bold mt-2">
                    {data.price.free ? "Free" : `$${data.price.price}`}
                  </div>
                </div>
              </div>
              <div className="mt-4 text-xs text-center text-gray-700">
                Payment secured by <span className="font-bold">STRIPE</span>.
                You'll be taken to a thank you page after the payment.
                <Link
                  to="/terms-and-privacy"
                  className="block mt-2 text-blue-600 hover:underline"
                >
                  Terms and Privacy
                </Link>
              </div>
            </div>

            {/* Payment Form Section */}
            <div className="w-full md:w-1/2 bg-primary-200 rounded-xl p-4 md:p-6">
              {/*<div className="w-full md:w-1/2 bg-gray-200 rounded-xl p-4 md:p-6"> */}
              {imageError ? (
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-4 text-red-600">
                    Error with this Envolvement
                  </h2>
                  <p>Error with Image, please contact Envolvly Support Team</p>
                </div>
              ) : data.quantity <= 0 ? (
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-4">Sold Out</h2>
                  <p>This envolvement is no longer available.</p>
                </div>
              ) : !data.active ? (
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-4">
                    Envolvement Inactive
                  </h2>
                  <p>This envolvement is not active and cannot be purchased.</p>
                </div>
              ) : (data.endDate?.endDate ? new Date(data.endDate.endDate) : new Date(data.endDate)) < new Date() ? (
                <div className="text-center">
                  <h2 className="text-xl font-semibold mb-4">
                    Purchase Period Ended
                  </h2>
                  <p>This envolvement is past the purchasable date.</p>
                </div>
              ) : isFree ? (
                <FreeCheckout />
              ) : (
                clientSecret &&
                stripePromise && (
                  <Elements
                    stripe={stripePromise}
                    options={{ clientSecret, appearance: { theme: "stripe" } }}
                  >
                    <CheckoutForm price={data.price.price} />
                  </Elements>
                )
              )}
            </div>
          </div>
        </div>
      )}
      {isAuthenticated && !data && (
        <div className="container mx-auto px-4 py-6 text-center">
          <div className="text-xl text-red-600">Envolvement ID not found</div>
        </div>
      )}
    </>
  );
}

function CheckoutForm(props: PaymentProps) {
  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState<String | undefined | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment/completion`,
      },
    });

    if (error.type === "card_error" || error.type === "validation_error") {
      setMessage(error.message);
    } else {
      setMessage("An unexpected error occurred.");
    }

    setIsProcessing(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement id="payment-element" />
      <button
        // className="w-full py-3 px-4 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        className="w-full py-3 px-4 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={isProcessing || !stripe || !elements}
        id="submit"
      >
        <span id="button-text">
          {isProcessing ? "Processing..." : `Pay $${props.price} now!`}
        </span>
      </button>
      {message && (
        <div
          id="payment-message"
          className="text-red-600 text-sm text-center mt-2"
        >
          {message}
        </div>
      )}
    </form>
  );
}

function FreeCheckout() {
  const { getAccessTokenSilently, user } = useAuth0();
  const { id } = useParams();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const handleFreeCheckout = async () => {
    setIsProcessing(true);
    setMessage(null);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(
        `${backend_domain}${backend_port}/payment/grant-access`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ submissionId: id, userId: user?.sub }),
        }
      );

      if (response.ok) {
        setMessage("Success! You now have access to this envolvement.");
        window.location.href = `/envolvement/${id}`;
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || "An error occurred.");
      }
    } catch (error) {
      setMessage("An unexpected error occurred.");
    }

    setIsProcessing(false);
  };

  return (
    <div className="text-center">
      <h2 className="text-xl font-semibold mb-4">This envolvement is free!</h2>
      <p className="mb-6">Click the button below to get access.</p>
      <button
        onClick={handleFreeCheckout}
        disabled={isProcessing}
        className="w-full py-3 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isProcessing ? "Processing..." : "Get Access Now"}
      </button>
      {message && (
        <div className="text-red-600 text-sm text-center mt-2">{message}</div>
      )}
    </div>
  );
}
